/**
 * Check-In Validators
 *
 * Validation functions for check-in creation requests and state transitions.
 * Used by CheckInService and Cloud Functions for input validation.
 *
 * @see WI-020 - Check-In Data Models
 * @see checkin.models.ts - CheckIn interfaces
 */

import { ExtendedCheckInStatus, CreateCheckInRequest } from './checkin.models';

/**
 * CheckInValidators
 * Static class containing validation functions for check-in operations.
 */
export class CheckInValidators {
  /**
   * Validate CreateCheckInRequest payload.
   * Checks trigger time, grace window, nudge delay, and note constraints.
   *
   * Validation Rules:
   * - Trigger time must be in the future (>= now)
   * - Trigger time cannot be more than 30 days in the future
   * - Grace window: 15-120 minutes (if provided)
   * - Nudge delay: 5-60 minutes (if provided)
   * - Note: max 500 characters (if provided)
   *
   * @param request - CreateCheckInRequest to validate
   * @returns Array of error messages (empty if valid)
   */
  static validateCreateRequest(request: CreateCheckInRequest): string[] {
    const errors: string[] = [];

    // Trigger time validation
    if (!request.triggerTime) {
      errors.push('Trigger time is required');
    } else {
      const now = new Date();
      if (request.triggerTime < now) {
        errors.push('Trigger time must be in the future');
      }

      const maxFuture = new Date(now.getTime() + 30 * 24 * 60 * 60 * 1000); // 30 days
      if (request.triggerTime > maxFuture) {
        errors.push('Trigger time cannot be more than 30 days in the future');
      }
    }

    // Grace window validation
    if (request.graceWindow !== undefined) {
      if (request.graceWindow < 15 || request.graceWindow > 120) {
        errors.push('Grace window must be between 15 and 120 minutes');
      }
    }

    // Nudge delay validation
    if (request.nudgeDelay !== undefined) {
      if (request.nudgeDelay < 5 || request.nudgeDelay > 60) {
        errors.push('Nudge delay must be between 5 and 60 minutes');
      }
    }

    // Note validation
    if (request.note && request.note.length > 500) {
      errors.push('Note cannot exceed 500 characters');
    }

    return errors;
  }

  /**
   * Validate check-in status transition.
   * Ensures state machine rules are followed (prevents invalid transitions).
   *
   * Valid Transitions (Phase 3+):
   * - scheduled → active, cancelled, resolved (resolved = early check-in)
   * - active → resolved, cancelled, snoozed
   * - nudge → resolved, escalating, snoozed
   * - escalating → resolved, completed
   * - snoozed → scheduled (when snooze expires)
   * - resolved → scheduled (undo accidental check-in, within time limit)
   * - completed → (terminal, no transitions)
   * - cancelled → (terminal, no transitions)
   *
   * Note: scheduled → resolved allows users to check in early if they
   * arrive before the scheduled time or if plans change.
   *
   * Note: resolved → scheduled allows users to undo an accidental check-in.
   * The UI should enforce a time limit (e.g., 5 minutes) to prevent abuse.
   *
   * @param currentStatus - Current ExtendedCheckInStatus
   * @param newStatus - Requested new ExtendedCheckInStatus
   * @returns true if transition is valid, false otherwise
   */
  static validateStatusTransition(
    currentStatus: ExtendedCheckInStatus,
    newStatus: ExtendedCheckInStatus
  ): boolean {
    const validTransitions: Record<ExtendedCheckInStatus, ExtendedCheckInStatus[]> = {
      scheduled: ['active', 'cancelled', 'resolved'],  // resolved: early check-in (arrived early, plans changed)
      active: ['resolved', 'cancelled', 'snoozed', 'nudge'],
      nudge: ['resolved', 'escalating', 'snoozed'],
      escalating: ['resolved', 'completed'],
      snoozed: ['scheduled', 'active'],  // Server transitions snoozed → scheduled when time expires
      resolved: ['scheduled'],  // Undo accidental check-in (UI enforces time limit)
      completed: [],  // Terminal state - no transitions allowed
      cancelled: [],  // Terminal state - no transitions allowed
    };

    return validTransitions[currentStatus]?.includes(newStatus) || false;
  }
}
